import type { FunctionComponent } from 'react';
import { FormattedMessage } from 'react-intl';

import { bitbucketPowerUpId, firstPartyPluginsOrg } from '@trello/config';

import type { Plugin } from './types';

import styles from './DataConsent.less';

// eslint-disable-next-line @trello/assets-alongside-implementation
import compliant from 'resources/images/directory/icons/compliant.svg';
// eslint-disable-next-line @trello/assets-alongside-implementation
import notCompliant from 'resources/images/directory/icons/notCompliant.svg';

const atlassianPowerUps = [bitbucketPowerUpId];

interface DataConsentProps {
  readonly plugin: Plugin;
}

const isTrelloOwned = (plugin: Plugin): boolean =>
  plugin.idOrganizationOwner === firstPartyPluginsOrg;

const isAtlassianOwned = (plugin: Plugin): boolean =>
  atlassianPowerUps.includes(plugin.id);

const ComplianceNotice: FunctionComponent<DataConsentProps> = ({ plugin }) => {
  let message;
  let isCompliant;
  const pluginName = plugin.listing?.name ?? '';

  if (plugin.isCompliantWithPrivacyStandards === false) {
    message = (
      <FormattedMessage
        tagName="span"
        id="templates.directory_data_consent.power-up-does-not-comply-with-our-data-privacy-standards"
        defaultMessage="{pluginName} may store personal data and is not in compliance with <a>Trello's data and privacy practices</a>."
        description="Power-Up does not comply with our data privacy"
        values={{
          pluginName,
          a: (chunks) => (
            <a href="https://trello.com/privacy" target="_blank">
              {chunks}
            </a>
          ),
        }}
      />
    );
    isCompliant = false;
  } else {
    isCompliant = true;

    if (plugin.isCompliantWithPrivacyStandards === true) {
      message = (
        <FormattedMessage
          tagName="span"
          id="templates.directory_data_consent.power-up-stores-data-complies-with-our-data-privacy-standards"
          defaultMessage="{pluginName} stores personal data and is in compliance with <a>Trello's data and privacy practices</a>."
          description="Power-Up stores data and complies with our data privacy"
          values={{
            pluginName,
            a: (chunks) => (
              <a href="https://trello.com/privacy" target="_blank">
                {chunks}
              </a>
            ),
          }}
        />
      );
    } else {
      message = (
        <FormattedMessage
          tagName="span"
          id="templates.directory_data_consent.power-up-complies-with-our-data-privacy-standards"
          defaultMessage="{pluginName} is in compliance with <a>Trello's data and privacy practices</a>."
          description="Power-Up complies with our data privacy"
          values={{
            pluginName,
            a: (chunks) => (
              <a href="https://trello.com/privacy" target="_blank">
                {chunks}
              </a>
            ),
          }}
        />
      );
    }
  }

  return (
    <div className={styles.directoryCompliance}>
      <p>
        <img
          src={isCompliant ? compliant : notCompliant}
          alt=""
          role="presentation"
        />
        {message}
      </p>
    </div>
  );
};

const PermissionsList: FunctionComponent<DataConsentProps> = ({ plugin }) => (
  <>
    <p>
      <FormattedMessage
        id="templates.directory_data_consent.by-you-clicking-add"
        defaultMessage="By you clicking add, {pluginName} will be able to:"
        description="Explanation of power-up permissions when added to a board"
        values={{
          pluginName: plugin.listing?.name ?? '',
        }}
      />
    </p>
    <ul>
      <li>
        <FormattedMessage
          id="templates.directory_data_consent.access-this-board"
          defaultMessage="Access this board"
          description="Access the board"
        />
      </li>
      <li>
        <FormattedMessage
          id="templates.directory_data_consent.add-content-to-this-board-such-as-attachments-to-cards"
          defaultMessage="Add content to this board such as attachments to cards"
          description="Add content to the board"
        />
      </li>
      <li>
        <FormattedMessage
          id="templates.directory_data_consent.take-actions-on-this-board-such-as-moving-sorting-filtering-cards-or-lists"
          defaultMessage="Take actions on this board such as moving, sorting, or filtering cards or lists"
          description="Take actions on the board"
        />
      </li>
      <li>
        <FormattedMessage
          id="templates.directory_data_consent.access-user-information-such-as-username-avatar-initials-and-full-name-for-users-on-this-board"
          defaultMessage="Access user information such as username, avatar, initials, and full name for users on this board"
          description="Access user information"
        />
      </li>
    </ul>
  </>
);

const PrivacyPolicy: FunctionComponent<DataConsentProps> = ({ plugin }) => {
  if (plugin.privacyUrl) {
    return (
      <FormattedMessage
        tagName="span"
        id="templates.directory_data_consent.for-more-information-please-see-power-ups-privacy-policy"
        defaultMessage="For more information, please see {pluginName}'s <a>privacy policy</a>."
        description="For more information, please see the Power-Up privacy policy"
        values={{
          pluginName: plugin.listing?.name ?? '',
          a: (chunks) => (
            <a href={plugin.privacyUrl ?? ''} target="_blank">
              {chunks}
            </a>
          ),
        }}
      />
    );
  }

  return (
    <FormattedMessage
      id="templates.directory_data_consent.for-more-information-please-see-power-ups-privacy-practices"
      defaultMessage="For more information, please see {pluginName}’s privacy practices."
      description="For more information, please see the Power-Up privacy practices"
      values={{
        pluginName: plugin.listing ? plugin.listing.name : '',
      }}
    />
  );
};

const AtlassianDataConsent: FunctionComponent<DataConsentProps> = ({
  plugin,
}) => {
  const pluginName = plugin.listing ? plugin.listing.name : '';

  return (
    <div className={styles.flexAndCenter}>
      <div className={styles.dataConsent}>
        <p>
          <FormattedMessage
            id="templates.directory_data_consent.power-up-is-developed-and-maintained-by-another-company-within-the-atlassian-corporate-family"
            defaultMessage="{pluginName} is developed and maintained by another company within the Atlassian corporate family."
            description="Power-Up is developed and maintained by another company within the Atlassian corporate family"
            values={{
              pluginName,
            }}
          />
        </p>
        <PermissionsList plugin={plugin} />
        <PrivacyPolicy plugin={plugin} />
      </div>
    </div>
  );
};

const ThirdPartyDataConsent: FunctionComponent<DataConsentProps> = ({
  plugin,
}) => {
  const pluginName = plugin.listing ? plugin.listing.name : '';

  return (
    <div className={styles.flexAndCenter}>
      <div className={styles.dataConsent}>
        <p>
          <FormattedMessage
            id="templates.directory_data_consent.power-up-is-not-developed-or-maintained-by-trello"
            defaultMessage="{pluginName} is not developed or maintained by Trello, and Trello is not responsible for its operation or security."
            description="Power-Up is not developed or maintained by Trello"
            values={{
              pluginName,
            }}
          />
        </p>
        {plugin.public && <ComplianceNotice plugin={plugin} />}
        <PermissionsList plugin={plugin} />
        <PrivacyPolicy plugin={plugin} />
      </div>
    </div>
  );
};

export const DataConsent: FunctionComponent<DataConsentProps> = ({
  plugin,
}) => {
  if (isTrelloOwned(plugin)) {
    return null;
  }

  if (isAtlassianOwned(plugin)) {
    return <AtlassianDataConsent plugin={plugin} />;
  }

  return <ThirdPartyDataConsent plugin={plugin} />;
};
